import {
  ButtonComponentV2,
  CardNetwork,
  FundingAmountOption,
  GooglePayAuthMethod,
  GooglePayFundingOptionsResult,
  GooglePayFundingOptionsResultTypenames,
  LimitInformation,
} from '@varo/graphql-schema-types';
import { useMemo } from 'react';
import GooglePayFundingOptions from 'services/graphql/queries/MoveMoney/GooglePayFundingOptions';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { RequestPolicy, useQuery } from 'urql';

export interface GooglePayFundingErrorScreen {
  screenType: ErrorScreenType;
  messageHeadline: string;
  messageBody: string;
  primaryButton?: ButtonComponentV2;
}

export type GooglePayErrorType = 'not_eligible' | 'error' | null;

interface GooglePayFundingOptionsData {
  isEligible: boolean;
  cardNetworks: CardNetwork[];
  gateway: string | null;
  gatewayMerchantId: string | null;
  allowedAuthMethods: GooglePayAuthMethod[];
  fundingOptions: FundingAmountOption[];
  limits: {
    minAmount: number;
    maxAmount: number;
    limitInfo: string | null;
    transferLimitErrorMessage: string | null;
  };
  limitInformation: {
    dailyLimitInformation: LimitInformation | null;
    monthlyLimitInformation: LimitInformation | null;
    disclaimer: string | null;
  };
}

interface UseGooglePayFundingOptionsReturn {
  fetching: boolean;
  errorScreen: GooglePayFundingErrorScreen | null;
  errorType: GooglePayErrorType;
  data: GooglePayFundingOptionsData;
}

const useGooglePayFundingOptions = (
  requestPolicy: RequestPolicy = 'network-only',
  pause: boolean = false,
): UseGooglePayFundingOptionsReturn => {
  const [{ data, error, fetching }] = useQuery<{
    googlePayFundingOptions: GooglePayFundingOptionsResult;
  }>({
    query: GooglePayFundingOptions,
    requestPolicy,
    pause,
  });

  const responseError = useFormattedCombinedError(error);
  const googlePayFundingOptionsData = data?.googlePayFundingOptions;

  const typename = googlePayFundingOptionsData?.__typename;
  const isSuccess =
    typename ===
    GooglePayFundingOptionsResultTypenames.GooglePayFundingOptionsSuccess;
  const isNotEligible =
    typename ===
    GooglePayFundingOptionsResultTypenames.GooglePayFundingOptionsNotEligible;
  const isError =
    typename ===
    GooglePayFundingOptionsResultTypenames.GooglePayFundingOptionsError;

  const successData = isSuccess ? googlePayFundingOptionsData : undefined;
  const notEligibleData = isNotEligible
    ? googlePayFundingOptionsData
    : undefined;
  const errorData = isError ? googlePayFundingOptionsData : undefined;

  const errorScreen = useMemo(() => {
    if (responseError) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      };
    }
    if (errorData) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: errorData.title,
        messageBody: errorData.message,
      };
    }
    if (notEligibleData) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: notEligibleData.title,
        messageBody: notEligibleData.message,
        primaryButton: notEligibleData.button,
      };
    }
    return null;
  }, [responseError, errorData, notEligibleData]);

  const errorType: GooglePayErrorType = useMemo(() => {
    if (isNotEligible) {
      return 'not_eligible';
    }
    if (isError || responseError) {
      return 'error';
    }
    return null;
  }, [isNotEligible, isError, responseError]);

  const {
    cardNetworks = [],
    gateway = null,
    gatewayMerchantId = null,
    allowedAuthMethods = [],
    limitInfo = null,
    minAmount,
    maxAmount,
    options: fundingOptions = [],
    transferLimitErrorMessage = null,
    dailyLimitInformation = null,
    monthlyLimitInformation = null,
    disclaimer = null,
  } = successData ?? {};

  return {
    fetching,
    errorScreen,
    errorType,
    data: {
      isEligible: isSuccess,
      cardNetworks,
      gateway,
      gatewayMerchantId,
      allowedAuthMethods,
      fundingOptions,
      limits: {
        minAmount: Number(minAmount?.amount ?? 0),
        maxAmount: Number(maxAmount?.amount ?? 0),
        limitInfo,
        transferLimitErrorMessage,
      },
      limitInformation: {
        dailyLimitInformation,
        monthlyLimitInformation,
        disclaimer,
      },
    },
  };
};

export default useGooglePayFundingOptions;
